import React, { useState, useEffect, useCallback, useRef } from "react";
import { Spinner } from "react-bootstrap";
import { Search, SlidersHorizontal, ArrowUpDown, ChevronDown, X } from "lucide-react";
import CustomPagination from "../common/CustomPagination.jsx";
import { DateRangePicker } from "react-date-range";
import moment from "moment";
import { getHandoverTicketsApi } from "./handover-api";
import calendarIcon from "../../Assets/svg/calendar.svg";
import { ReactComponent as CalendarPurpleIcon } from "../../Assets/svg/calendar-purple.svg";

const renderStatusBadge = (status) => {
  const st = (status || "").toLowerCase();
  let cssClass = "open";
  if (st.includes("progress")) cssClass = "in-progress";
  else if (st.includes("hold")) cssClass = "hold";
  else if (st.includes("closed")) cssClass = "closed";
  else if (st.includes("completed")) cssClass = "completed";

  return (
    <span className={`status-badge ${cssClass}`}>
      <span className="status-dot"></span>
      {status || "Open"}
    </span>
  );
};

const HandoverList = ({
  fromUser,
  checkAssigned,
  checkReporter,
  checkCreated,
  selectedTicketIds,
  onSelectTicket,
  onSelectAll,
  onOpenFilterDrawer,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  expStartDate,
  setExpStartDate,
  expEndDate,
  setExpEndDate,
  selectedProjectOptions,
  selectedTeamOptions,
  setSelectedProjectOptions,
  setSelectedTeamOptions,
}) => {
  const [statusFilter, setStatusFilter] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [tickets, setTickets] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [loadingTickets, setLoadingTickets] = useState(false);

  // Date Pickers State
  const [showPicker, setShowPicker] = useState(false);
  const [dateRangeState, setDateRangeState] = useState([
    { startDate: new Date(), endDate: new Date(), key: "selection" },
  ]);
  const [showExpPicker, setShowExpPicker] = useState(false);
  const [expDateRangeState, setExpDateRangeState] = useState([
    { startDate: new Date(), endDate: new Date(), key: "selection" },
  ]);

  const datePickerRef = useRef(null);
  const expDatePickerRef = useRef(null);
  const statusTabRefs = useRef({});
  const [statusPillStyle, setStatusPillStyle] = useState({ left: 4, width: 0, height: 0 });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        setShowPicker(false);
      }
      if (expDatePickerRef.current && !expDatePickerRef.current.contains(event.target)) {
        setShowExpPicker(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const el = statusTabRefs.current[statusFilter];
    if (el) {
      setStatusPillStyle({
        left: el.offsetLeft,
        width: el.offsetWidth,
        height: el.offsetHeight,
      });
    }
  }, [statusFilter, totalRecords]);

  const getFilterCount = () => {
    let count = 0;
    if (startDate || endDate) count++;
    if (expStartDate || expEndDate) count++;
    if (selectedProjectOptions && selectedProjectOptions.length > 0) count++;
    if (selectedTeamOptions && selectedTeamOptions.length > 0) count++;
    return count;
  };

  const fetchTickets = useCallback(async () => {
    if (!fromUser) {
      setTickets([]);
      setTotalRecords(0);
      return;
    }

    setLoadingTickets(true);
    try {
      const skip = (currentPage - 1) * recordsPerPage;
      const params = {
        from_user_id: fromUser,
        check_assigned_to: checkAssigned,
        check_reporter: checkReporter,
        check_created_by: checkCreated,
        status: statusFilter === "All" ? ["All"] : [statusFilter],
        search: searchTerm,
        limit: recordsPerPage,
        skip: skip,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        exp_start_date: expStartDate || undefined,
        exp_end_date: expEndDate || undefined,
      };

      const res = await getHandoverTicketsApi(params);
      if (res?.data?.success) {
        setTickets(res.data.tickets || []);
        setTotalRecords(res.data.total_rec || 0);
      } else {
        setTickets([]);
        setTotalRecords(0);
      }
    } catch (err) {
      console.error("Error fetching handover tickets:", err);
      setTickets([]);
    } finally {
      setLoadingTickets(false);
    }
  }, [
    fromUser,
    checkAssigned,
    checkReporter,
    checkCreated,
    statusFilter,
    searchTerm,
    currentPage,
    recordsPerPage,
    startDate,
    endDate,
    expStartDate,
    expEndDate,
  ]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const renderTableContent = () => {
    if (loadingTickets) {
      return (
        <tr>
          <td colSpan="9" className="text-center py-5">
            <div className="d-flex flex-column align-items-center justify-content-center gap-3 py-3">
              <Spinner animation="border" style={{ color: "#6929e2", width: "2.2rem", height: "2.2rem", borderWidth: "3px" }} />
              <span style={{ fontSize: "14px", color: "#697386", fontWeight: "500" }}>Loading tickets...</span>
            </div>
          </td>
        </tr>
      );
    }
    if (!fromUser) {
      return (
        <tr>
          <td colSpan="9" className="text-center py-4 text-muted">
            Please select a "From user" above to view tickets for handover.
          </td>
        </tr>
      );
    }
    if (tickets.length === 0) {
      return (
        <tr>
          <td colSpan="9" className="text-center py-4 text-muted">
            No tickets found for handover.
          </td>
        </tr>
      );
    }
    return tickets.map((ticket) => (
      <tr key={ticket.id}>
        <td>
          <input
            type="checkbox"
            checked={selectedTicketIds.includes(ticket.id)}
            onChange={() => onSelectTicket(ticket.id)}
          />
        </td>
        <td style={{ fontWeight: "600", color: "#1a1f36" }}>{ticket.code}</td>
        <td>{ticket.title || ticket.task_title || "-"}</td>
        <td>{ticket.project || ticket.project_name || "-"}</td>
        <td>{ticket.module || ticket.module_name || "-"}</td>
        <td>{ticket.assigned_to || "-"}</td>
        <td>{ticket.reporter || "-"}</td>
        <td>{ticket.expected_date || ticket.date || "-"}</td>
        <td>{renderStatusBadge(ticket.status)}</td>
      </tr>
    ));
  };

  return (
    <div className="handover-table-card">
      <div className="handover-table-toolbar">
        
        <div className="status-pill-group">
          <div
            className="status-pill-indicator"
            style={{
              left: `${statusPillStyle.left}px`,
              width: `${statusPillStyle.width}px`,
              height: `${statusPillStyle.height}px`,
            }}
          />
          {["All", "Open", "Hold", "Closed", "Completed"].map((st) => (
            <button
              key={st}
              ref={(el) => (statusTabRefs.current[st] = el)}
              className={`status-pill-btn ${statusFilter === st ? "active" : ""}`}
              onClick={() => {
                setStatusFilter(st);
                setCurrentPage(1);
              }}
            >
              {statusFilter === st ? `${st} (${totalRecords})` : st}
            </button>
          ))}
        </div>

        
        <div className="table-toolbar-right">
          
          <div ref={datePickerRef} style={{ height: "40px", flexShrink: 0, position: "relative" }}>
            <div
              onClick={() => {
                setShowPicker(!showPicker);
                setShowExpPicker(false);
              }}
              className="date-filter-btn"
              style={{
                border: startDate || endDate ? "1px solid #6929E2" : "1px solid #E5E5E5",
                cursor: "pointer",
                height: "40px",
                borderRadius: "100px",
                padding: "0 16px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                backgroundColor: "#FFFFFF",
              }}
            >
              {startDate || endDate ? (
                <CalendarPurpleIcon style={{ width: "16px", height: "16px" }} />
              ) : (
                <img src={calendarIcon} alt="calendar" style={{ width: "16px", height: "16px" }} />
              )}
              <span
                style={{
                  fontFamily: "Satoshi",
                  fontSize: "14px",
                  fontWeight: startDate || endDate ? "600" : "500",
                  color: startDate || endDate ? "#6929E2" : "#212129",
                }}
              >
                {startDate && endDate ? `${moment(startDate).format("DD MMM")} - ${moment(endDate).format("DD MMM")}` : "Date Range"}
              </span>
              {(startDate || endDate) && (
                <X
                  size={14}
                  color="#6929e2"
                  style={{ cursor: "pointer", marginLeft: "4px" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setStartDate("");
                    setEndDate("");
                    setDateRangeState([{ startDate: new Date(), endDate: new Date(), key: "selection" }]);
                  }}
                />
              )}
            </div>
            {showPicker && (
              <div
                style={{
                  position: "absolute",
                  top: "48px",
                  right: "0",
                  zIndex: 300,
                  background: "#fff",
                  border: "1px solid #ccc",
                  borderRadius: "12px",
                  boxShadow: "0px 4px 24px rgba(0,0,0,0.15)",
                  padding: "8px",
                }}
              >
                <DateRangePicker
                  ranges={dateRangeState}
                  onChange={(item) => {
                    setStartDate(moment(item.selection.startDate).format("YYYY-MM-DD"));
                    setEndDate(moment(item.selection.endDate).format("YYYY-MM-DD"));
                    setDateRangeState([item.selection]);
                  }}
                  showSelectionPreview={true}
                  moveRangeOnFirstSelection={false}
                  months={1}
                  direction="horizontal"
                />
              </div>
            )}
          </div>

          
          <div ref={expDatePickerRef} style={{ height: "40px", flexShrink: 0, position: "relative" }}>
            <div
              onClick={() => {
                setShowExpPicker(!showExpPicker);
                setShowPicker(false);
              }}
              className="date-filter-btn"
              style={{
                border: expStartDate || expEndDate ? "1px solid #6929E2" : "1px solid #E5E5E5",
                cursor: "pointer",
                height: "40px",
                borderRadius: "100px",
                padding: "0 16px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                backgroundColor: "#FFFFFF",
              }}
            >
              {expStartDate || expEndDate ? (
                <CalendarPurpleIcon style={{ width: "16px", height: "16px" }} />
              ) : (
                <img src={calendarIcon} alt="calendar" style={{ width: "16px", height: "16px" }} />
              )}
              <span
                style={{
                  fontFamily: "Satoshi",
                  fontSize: "14px",
                  fontWeight: expStartDate || expEndDate ? "600" : "500",
                  color: expStartDate || expEndDate ? "#6929E2" : "#212129",
                }}
              >
                {expStartDate && expEndDate
                  ? `${moment(expStartDate).format("DD MMM")} - ${moment(expEndDate).format("DD MMM")}`
                  : "Exp. date"}
              </span>
              {(expStartDate || expEndDate) && (
                <X
                  size={14}
                  color="#6929e2"
                  style={{ cursor: "pointer", marginLeft: "4px" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpStartDate("");
                    setExpEndDate("");
                  }}
                />
              )}
            </div>
            {showExpPicker && (
              <div
                style={{
                  position: "absolute",
                  top: "48px",
                  right: "0",
                  zIndex: 300,
                  background: "#fff",
                  border: "1px solid #ccc",
                  borderRadius: "12px",
                  boxShadow: "0px 4px 24px rgba(0,0,0,0.15)",
                  padding: "8px",
                }}
              >
                <DateRangePicker
                  ranges={expDateRangeState}
                  onChange={(item) => {
                    setExpStartDate(moment(item.selection.startDate).format("YYYY-MM-DD"));
                    setExpEndDate(moment(item.selection.endDate).format("YYYY-MM-DD"));
                    setExpDateRangeState([item.selection]);
                  }}
                  showSelectionPreview={true}
                  moveRangeOnFirstSelection={false}
                  months={1}
                  direction="horizontal"
                />
              </div>
            )}
          </div>

          
          <div className="search-input-wrapper" style={{ width: "200px", minWidth: "160px", position: "relative" }}>
            <Search className="search-icon" size={15} color="#8790a1" />
            <input
              type="text"
              placeholder="Search tasks..."
              style={{
                width: "100%",
                height: "40px",
                borderRadius: "100px",
                border: "1px solid #E5E5E5",
                paddingLeft: "40px",
                fontSize: "14px",
                color: "#212129",
                outline: "none",
              }}
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>

          
          <div style={{ position: "relative", flexShrink: 0 }}>
            <div
              onClick={onOpenFilterDrawer}
              style={{
                height: "40px",
                borderRadius: "100px",
                border: getFilterCount() > 0 ? "1px solid #6929E2" : "1px solid #E5E5E5",
                backgroundColor: "#FFFFFF",
                display: "flex",
                alignItems: "center",
                padding: "0 14px",
                cursor: "pointer",
                gap: "8px",
              }}
            >
              <SlidersHorizontal size={16} color={getFilterCount() > 0 ? "#6929e2" : "#3c4257"} />
              {getFilterCount() > 0 && (
                <div
                  style={{
                    width: "24px",
                    height: "24px",
                    borderRadius: "50%",
                    backgroundColor: "#F3EDFF",
                    color: "#6929E2",
                    fontSize: "13px",
                    fontWeight: "700",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontFamily: "Satoshi",
                  }}
                >
                  {getFilterCount()}
                </div>
              )}
            </div>
            {getFilterCount() > 0 && (
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  if (setStartDate) setStartDate("");
                  if (setEndDate) setEndDate("");
                  if (setExpStartDate) setExpStartDate("");
                  if (setExpEndDate) setExpEndDate("");
                  if (setSelectedProjectOptions) setSelectedProjectOptions([]);
                  if (setSelectedTeamOptions) setSelectedTeamOptions([]);
                }}
                style={{
                  position: "absolute",
                  top: "-6px",
                  right: "-4px",
                  width: "22px",
                  height: "22px",
                  borderRadius: "50%",
                  backgroundColor: "#FF4444",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  border: "2px solid #FFFFFF",
                  zIndex: 10,
                }}
                title="Clear filter"
              >
                <X size={10} color="#FFFFFF" strokeWidth={3} />
              </div>
            )}
          </div>

          
          <div style={{ position: "relative", flexShrink: 0 }}>
            <div
              style={{
                height: "40px",
                borderRadius: "100px",
                border: "1px solid #E5E5E5",
                backgroundColor: "#FFFFFF",
                display: "flex",
                alignItems: "center",
                padding: "0 16px",
                cursor: "pointer",
                gap: "8px",
                whiteSpace: "nowrap",
              }}
            >
              <ArrowUpDown size={15} color="#6929e2" />
              <span style={{ fontFamily: "Satoshi", fontSize: "14px", fontWeight: "700", color: "#212129" }}>
                Default <span style={{ fontWeight: "400", color: "#8790A1" }}>(Created)</span>
              </span>
              <ChevronDown size={14} color="#3c4257" />
            </div>
          </div>
        </div>
      </div>

      
      <div className="handover-table-responsive">
        <table className="handover-table">
          <thead>
            <tr>
              <th style={{ width: "40px" }}>
                <input
                  type="checkbox"
                  checked={tickets.length > 0 && selectedTicketIds.length === tickets.length}
                  onChange={(e) => onSelectAll(e, tickets)}
                  disabled={tickets.length === 0}
                />
              </th>
              <th>Code</th>
              <th>Task Title</th>
              <th>Project</th>
              <th>Module</th>
              <th>Assigned to</th>
              <th>Reporter</th>
              <th>Expected date</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>{renderTableContent()}</tbody>
        </table>
      </div>

      
      <CustomPagination
        currentPage={currentPage}
        totalRecords={totalRecords}
        recordsPerPage={recordsPerPage}
        onPageChange={setCurrentPage}
        onRecordsPerPageChange={(newSize) => {
          setRecordsPerPage(newSize);
          setCurrentPage(1);
        }}
        labelText="Records per page "
      />
    </div>
  );
};

export default HandoverList;
